// Trading VPS — direct execution-host health from the one-second guard heartbeat. The only
// action here is the admin-only "Remove" for retired hosts: it deletes the guard's own
// health rows server-side (audit-logged), never a terminal command or a trade. A guard that
// is still running re-registers on its next heartbeat.

import { useState } from 'react';
import * as api from '../api/client';
import { ApiError } from '../api/http';
import { C, MONO } from '../lib/tokens';
import { fmtAge } from '../lib/format';
import {
  deriveTradingVpsState,
  sortTradingVps,
  summarizeTradingVps,
  tradingVpsStaleAgeS,
  type TradingVpsStateKey,
} from '../lib/tradingVps';
import { mTd, mTh, mono, GBTN_SM } from '../lib/styles';
import { EmptyState, Sub } from '../components/common';
import { useAuth, useData, useTradingVpsDataAgeS } from '../store/hooks';
import { dataStore, feed } from '../store/dataStore';
import { toastMsg } from '../store/uiStore';

const DANGER_SM: React.CSSProperties = { ...GBTN_SM, color: C.rd, border: '1px solid ' + C.rd + '77' };

function stateColor(key: TradingVpsStateKey): string {
  if (key === 'live') return C.gn;
  if (key === 'delayed' || key === 'algo_off') return C.am;
  return C.rd;
}

function boolState(value: boolean | null, on: string, off: string): [string, string] {
  if (value === true) return [`● ${on}`, C.gn];
  if (value === false) return [`✕ ${off}`, C.rd];
  return ['? UNKNOWN', C.gy];
}

/** Humanized "Last seen" — "12s ago", "3m 41s ago", "2h 5m ago", "1d 4h ago". */
function formatLastSeen(seconds: number | null): string {
  if (seconds == null) return 'never';
  const label = fmtAge(seconds * 1000);
  return label === 'now' ? 'just now' : label + ' ago';
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

export function TradingVps() {
  const rows = useData((state) => state.tradingVps);
  const error = useData((state) => state.tradingVpsError);
  const isAdmin = useAuth((s) => s.user?.role === 'admin');
  const dataAgeS = useTradingVpsDataAgeS();
  const staleAgeS = tradingVpsStaleAgeS(dataAgeS);

  // Admin removal: which host is awaiting confirmation, and which one is in flight.
  const [confirmHost, setConfirmHost] = useState<string | null>(null);
  const [busyHost, setBusyHost] = useState<string | null>(null);

  const ordered = sortTradingVps(rows, staleAgeS);
  const summary = summarizeTradingVps(rows, staleAgeS);
  const hostLabel = `${summary.total} execution ${summary.total === 1 ? 'host' : 'hosts'} · ${rows.length} MT5 ${rows.length === 1 ? 'terminal' : 'terminals'}`;

  // One Remove control per host (a shared VPS spans several terminal rows).
  const firstRowOfHost = new Map<string, string>();
  for (const row of ordered) {
    if (!firstRowOfHost.has(row.vps_id)) firstRowOfHost.set(row.vps_id, row.terminal_key);
  }

  const headers = [
    'Status', 'Trading VPS', 'Account', 'Windows host', 'MT5', 'Broker', 'Algo',
    'Positions', 'Last seen', 'Uptime', ...(isAdmin ? ['Remove'] : []),
  ];

  async function removeHost(vpsId: string): Promise<void> {
    if (busyHost) return;
    setBusyHost(vpsId);
    try {
      await api.removeTradingVps(vpsId);
      toastMsg(`Removed ${vpsId} from direct VPS monitoring.`);
      setConfirmHost(null);
      // Drop the host locally right away, then reconcile against the server.
      dataStore.set((s) => ({ tradingVps: s.tradingVps.filter((r) => r.vps_id !== vpsId) }));
      feed.refreshTradingVps();
    } catch (e) {
      toastMsg(`Could not remove ${vpsId}: ` + (e instanceof ApiError ? e.message : 'error'));
    } finally {
      setBusyHost(null);
    }
  }

  return (
    <section data-screen-label="Trading VPS" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Trading VPS</h1>
        <Sub>Direct execution-host monitoring · one-second heartbeat</Sub>
        <div style={{ flex: 1 }} />
        <div style={{ fontFamily: MONO, color: summary.issues ? C.rd : summary.total ? C.gn : C.gy, fontSize: 12, fontWeight: 600 }}>
          {summary.live} / {summary.total} live
        </div>
      </div>

      {(error != null || staleAgeS > 0) && rows.length > 0 && (
        <div role="status" style={{ color: C.am, background: 'rgba(224,168,62,.08)', border: '1px solid rgba(224,168,62,.35)', padding: '8px 12px', fontSize: 11.5 }}>
          {error != null
            ? 'Last update failed. Showing the most recent direct VPS state.'
            : `Direct VPS feed is stale — last update ${fmtAge(dataAgeS * 1000)} ago. Host statuses account for the delay.`}
        </div>
      )}

      {isAdmin && confirmHost != null && (
        <div role="alert" style={{ fontSize: 11, color: C.am, background: 'rgba(224,168,62,.08)', border: '1px solid rgba(224,168,62,.35)', padding: '8px 12px', lineHeight: 1.5 }}>
          Remove <strong>{confirmHost}</strong> from direct VPS monitoring? Its guard health rows are deleted. A guard that is still running will re-register on its next heartbeat.
        </div>
      )}

      <div style={{ background: C.s1, border: '1px solid ' + C.ln, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.ln2, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>{hostLabel}</span>
          <span style={{ fontSize: 11, color: C.dm }}>{summary.message}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="Trading VPS health" style={{ width: '100%', minWidth: isAdmin ? 1220 : 1120, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th key={header} scope="col" style={mTh(index === 7 ? 'right' : 'left')}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordered.map((row) => {
                const primary = deriveTradingVpsState(row, staleAgeS);
                const mt5: [string, string] = row.terminal_reachable ? ['● RUNNING', C.gn] : ['✕ DOWN', C.rd];
                const broker = boolState(row.connected, 'CONNECTED', 'OFF');
                const algo = boolState(row.trade_expert, 'ON', 'OFF');
                const showRemove = isAdmin && firstRowOfHost.get(row.vps_id) === row.terminal_key;
                return (
                  <tr key={`${row.vps_id}:${row.terminal_key}`} aria-label={`${row.vps_id} ${primary.label}`} title={row.last_error ?? undefined}>
                    <td style={mTd('left', mono(stateColor(primary.key)))}>{primary.label}</td>
                    <td style={mTd('left', mono(C.b9))}>
                      {row.vps_id}
                      {row.guard_version != null && row.guard_version !== '' && (
                        <span style={{ display: 'block', color: C.dm, fontSize: 10, marginTop: 2 }}>guard {row.guard_version}</span>
                      )}
                    </td>
                    <td style={mTd('left')}>
                      <span style={{ display: 'block', fontWeight: 600 }}>{row.account_label ?? 'Unmatched account'}</span>
                      <span style={{ display: 'block', color: C.dm, fontFamily: MONO, fontSize: 10, marginTop: 2 }}>{row.account_login ?? 'No login'}</span>
                    </td>
                    <td style={mTd('left', mono(C.b9))}>{row.machine_name}</td>
                    <td style={mTd('left', mono(mt5[1]))}>
                      {mt5[0]}{row.terminal_build != null && <span style={{ display: 'block', color: C.dm, fontSize: 10, marginTop: 2 }}>build {row.terminal_build}</span>}
                    </td>
                    <td style={mTd('left', mono(broker[1]))}>{broker[0]}</td>
                    <td style={mTd('left', mono(algo[1]))}>{algo[0]}</td>
                    <td style={mTd('right', mono(C.tx))}>{row.positions_count}</td>
                    <td style={mTd('left', mono(primary.issue ? stateColor(primary.key) : C.b9))}>
                      {formatLastSeen(row.age_s == null ? null : row.age_s + dataAgeS)}
                    </td>
                    <td style={mTd('left', mono(C.b9))}>{formatUptime(row.windows_uptime_s)}</td>
                    {isAdmin && (
                      <td style={mTd('left')}>
                        {showRemove &&
                          (confirmHost === row.vps_id ? (
                            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                              <button
                                onClick={() => void removeHost(row.vps_id)}
                                disabled={busyHost === row.vps_id}
                                style={{ ...DANGER_SM, opacity: busyHost === row.vps_id ? 0.6 : 1 }}
                              >
                                {busyHost === row.vps_id ? '…' : 'Yes, remove'}
                              </button>
                              <button onClick={() => setConfirmHost(null)} disabled={busyHost === row.vps_id} style={GBTN_SM}>
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setConfirmHost(row.vps_id)}
                              disabled={!!busyHost}
                              aria-label={'Remove ' + row.vps_id}
                              title="Remove this host from direct VPS monitoring — a running guard re-registers on its next heartbeat"
                              style={{ ...DANGER_SM, opacity: busyHost ? 0.6 : 1 }}
                            >
                              Remove
                            </button>
                          ))}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <EmptyState
            title={error ? 'Direct VPS health is unavailable' : 'No trading VPS guards reporting'}
            sub={error ? 'WATCHTOWER will keep retrying every second.' : 'Install WATCHTOWER Guard on an execution host to add it here.'}
          />
        )}
      </div>
      <div style={{ color: C.dm, fontFamily: MONO, fontSize: 10.5 }}>
        Read-only host and MT5 process health · no remote controls{isAdmin ? ' · Remove only deletes monitoring rows' : ''}
      </div>
    </section>
  );
}
